import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraInput, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Check } from 'lucide-react';
import { useState } from 'react';
import { useRfiFollowUpMutations, useRfiFollowUps } from './rfiSectionHooks';
/** Follow-ups belong to the RFI itself, not to any linked pursuit. */ export function RfiFollowUpsPanel({ rfiResponseProjectId, sections, people }) {
    const { data: followUps = [] } = useRfiFollowUps(rfiResponseProjectId);
    const { create, setStatus } = useRfiFollowUpMutations(rfiResponseProjectId);
    const [title, setTitle] = useState('');
    const [sectionId, setSectionId] = useState('');
    const [assigneeUserId, setAssigneeUserId] = useState('');
    const sectionTitle = (id)=>sections.find((s)=>s.id === id)?.title;
    const personName = (id)=>{
        const p = people.find((x)=>x.userId === id);
        return p ? p.name || p.email : id;
    };
    const handleAdd = (e)=>{
        e.preventDefault();
        const trimmed = title.trim();
        if (!trimmed) return;
        create.mutate({
            title: trimmed,
            sectionId,
            assigneeUserId
        }, {
            onSuccess: ()=>{
                setTitle('');
                setSectionId('');
                setAssigneeUserId('');
            }
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2 pt-2",
        id: "rfi-follow-ups",
        "data-testid": Testids.rfiOutline.followUpsPanel,
        children: [
            /*#__PURE__*/ _jsx("h3", {
                className: "text-base font-medium",
                children: "RFI follow-ups"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "These follow-ups belong to this RFI response, not to a linked pursuit."
            }),
            followUps.length === 0 ? /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "No follow-ups yet."
            }) : /*#__PURE__*/ _jsx("ul", {
                className: "divide-y rounded-md border",
                children: followUps.map((f)=>/*#__PURE__*/ _jsxs("li", {
                        className: "flex flex-wrap items-center gap-2 px-3 py-2",
                        "data-testid": Testids.rfiOutline.followUpRow,
                        id: `rfi-follow-up-${f.id}`,
                        "data-follow-up-id": f.id,
                        "data-status": f.status,
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: f.status === 'done' ? 'flex-1 line-through' : 'flex-1',
                                children: f.title
                            }),
                            f.sectionId && sectionTitle(f.sectionId) ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "neutral",
                                children: sectionTitle(f.sectionId)
                            }) : null,
                            f.assigneeUserId ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "info",
                                children: personName(f.assigneeUserId)
                            }) : null,
                            f.status === 'done' ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "success",
                                children: "Done"
                            }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "secondary",
                                disabled: setStatus.isPending,
                                onClick: ()=>setStatus.mutate({
                                        followUpId: f.id,
                                        status: 'done'
                                    }),
                                "data-testid": Testids.rfiOutline.followUpDoneButton,
                                iconLeft: /*#__PURE__*/ _jsx(Check, {
                                    className: "h-4 w-4"
                                }),
                                children: "Mark done"
                            })
                        ]
                    }, f.id))
            }),
            /*#__PURE__*/ _jsxs("form", {
                className: "flex flex-wrap items-center gap-2",
                onSubmit: handleAdd,
                children: [
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        className: "min-w-48 flex-1",
                        "aria-label": "Follow-up title",
                        placeholder: "New follow-up",
                        value: title,
                        onChange: (e)=>setTitle(e.target.value),
                        "data-testid": Testids.rfiOutline.followUpTitleInput
                    }),
                    /*#__PURE__*/ _jsxs(SpecraSelect, {
                        className: "w-44",
                        "aria-label": "Follow-up section",
                        value: sectionId,
                        onChange: (e)=>setSectionId(e.target.value),
                        "data-testid": Testids.rfiOutline.followUpSectionSelect,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "No section"
                            }),
                            sections.map((s)=>/*#__PURE__*/ _jsx("option", {
                                    value: s.id,
                                    children: s.title
                                }, s.id))
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(SpecraSelect, {
                        className: "w-44",
                        "aria-label": "Follow-up assignee",
                        value: assigneeUserId,
                        onChange: (e)=>setAssigneeUserId(e.target.value),
                        "data-testid": Testids.rfiOutline.followUpAssigneeSelect,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Unassigned"
                            }),
                            people.map((p)=>/*#__PURE__*/ _jsx("option", {
                                    value: p.userId,
                                    children: p.name || p.email
                                }, p.userId))
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "submit",
                        size: "sm",
                        variant: "secondary",
                        disabled: !title.trim() || create.isPending,
                        "data-testid": Testids.rfiOutline.addFollowUpButton,
                        children: "Add follow-up"
                    })
                ]
            })
        ]
    });
}
